/**
 * Batch Actions Bar Component
 * Quick actions for bulk operations on selected relationships
 */

import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Chip,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  FormControl,
  InputLabel,
  Alert,
  LinearProgress,
  Divider,
  Tooltip,
  Stack,
} from '@mui/material';
import {
  Check as CheckIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  MoreVert as MoreIcon,
  Download as DownloadIcon,
  Assignment as AssignmentIcon,
  Speed as SpeedIcon,
  FilterList as FilterIcon,
  SelectAll as SelectAllIcon,
  Deselect as DeselectIcon,
} from '@mui/icons-material';
import useTokenExaminerStore from '../stores/tokenExaminerStore';
import api from '../../../services/api';

const BULK_OPERATIONS = [
  {
    key: 'confirm',
    label: 'Confirm Selected',
    icon: <CheckIcon />,
    color: 'success',
    description: 'Mark selected relationships as confirmed',
  },
  {
    key: 'reject',
    label: 'Reject Selected',
    icon: <CloseIcon />,
    color: 'error',
    description: 'Mark selected relationships as rejected',
  },
  {
    key: 'edit_confidence',
    label: 'Update Confidence',
    icon: <EditIcon />,
    color: 'primary',
    description: 'Change confidence level for selected relationships',
  },
  {
    key: 'assign_reviewer',
    label: 'Assign Reviewer',
    icon: <AssignmentIcon />,
    color: 'secondary',
    description: 'Assign a reviewer to selected relationships',
  },
];

const CONFIDENCE_LEVELS = ['HIGH', 'MEDIUM', 'LOW'];

// eslint-disable-next-line max-lines-per-function
function BatchActionsBar({ selectedRelationships = [], onSelectionChange }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [operationDialog, setOperationDialog] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [operationResult, setOperationResult] = useState(null);

  // Dialog state
  const [newConfidence, setNewConfidence] = useState('MEDIUM');
  const [assigneeEmail, setAssigneeEmail] = useState('');
  const [operationNote, setOperationNote] = useState('');

  const {
    relationships,
    _entities,
    _selectedEntity,
    confirmRelationship,
    rejectRelationship,
    updateRelationship,
    _selectRelationships,
    getFilteredRelationships,
  } = useTokenExaminerStore();

  const filteredRelationships = getFilteredRelationships();
  const selectedCount = selectedRelationships.length;

  // Calculate selection stats
  const selectionStats = React.useMemo(() => {
    const confirmed = selectedRelationships.filter(id => {
      const rel = relationships.find(r => r.id === id);
      return rel?.isConfirmed;
    }).length;

    const unconfirmed = selectedCount - confirmed;

    const highConfidence = selectedRelationships.filter(id => {
      const rel = relationships.find(r => r.id === id);
      return rel?.confidence === 'HIGH';
    }).length;

    const criticalHealth = selectedRelationships.filter(id => {
      const rel = relationships.find(r => r.id === id);
      return (rel?.overallHealth || 0) < 40;
    }).length;

    return {
      confirmed,
      unconfirmed,
      highConfidence,
      criticalHealth,
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRelationships, relationships]);

  const handleSelectAll = () => {
    const allIds = filteredRelationships.map(rel => rel.id);
    onSelectionChange(allIds);
  };

  const handleDeselectAll = () => {
    onSelectionChange([]);
  };

  const handleSelectFiltered = (filterType) => {
    let filtered;

    switch (filterType) {
      case 'unconfirmed':
        filtered = filteredRelationships
          .filter(rel => !rel.isConfirmed)
          .map(rel => rel.id);
        break;
      case 'high_confidence':
        filtered = filteredRelationships
          .filter(rel => rel.confidence === 'HIGH')
          .map(rel => rel.id);
        break;
      case 'critical':
        filtered = filteredRelationships
          .filter(rel => (rel.overallHealth || 0) < 40)
          .map(rel => rel.id);
        break;
      default:
        filtered = [];
    }

    onSelectionChange(filtered);
    setAnchorEl(null);
  };

  const handleOperationClick = (operation) => {
    setOperationDialog(operation);
    setAnchorEl(null);
  };

  const handleConfirmOperation = async () => {
    if (!operationDialog || selectedCount === 0) return;

    setIsProcessing(true);
    setProcessingProgress(0);

    try {
      const total = selectedRelationships.length;
      let completed = 0;
      let successful = 0;
      let failed = 0;

      for (const relationshipId of selectedRelationships) {
        try {
          // eslint-disable-next-line max-depth
          switch (operationDialog.key) {
            case 'confirm':
              await confirmRelationship(relationshipId, 'batch-operation');
              successful++;
              break;

            case 'reject':
              await rejectRelationship(relationshipId, operationNote || 'Batch rejection');
              successful++;
              break;

            case 'edit_confidence':
              await updateRelationship(relationshipId, {
                confidence: newConfidence,
                note: operationNote || 'Batch confidence update',
              });
              successful++;
              break;

            case 'assign_reviewer':
              // This would call a specific API for assignment
              await api.assignRelationshipReviewer(relationshipId, assigneeEmail, {
                note: operationNote,
              });
              successful++;
              break;

            default:
              failed++;
          }
        } catch (error) {
          console.error(`Error processing relationship ${relationshipId}:`, error);
          failed++;
        }

        completed++;
        setProcessingProgress((completed / total) * 100);
      }

      setOperationResult({
        successful,
        failed,
        total,
        operation: operationDialog.label,
      });

      // Clear selection after successful batch operation
      if (successful > 0) {
        onSelectionChange([]);
      }

    } catch (error) {
      console.error('Batch operation failed:', error);
      setOperationResult({
        successful: 0,
        failed: selectedCount,
        total: selectedCount,
        operation: operationDialog.label,
        error: error.message,
      });
    } finally {
      setIsProcessing(false);
      setOperationDialog(null);

      // Reset form fields
      setNewConfidence('MEDIUM');
      setAssigneeEmail('');
      setOperationNote('');
    }
  };

  const handleExportSelected = async () => {
    try {
      const selectedData = selectedRelationships.map(id => {
        const rel = relationships.find(r => r.id === id);
        return {
          id: rel.id,
          type: rel.relationshipType,
          sourceEntity: rel.sourceEntity?.name,
          targetEntity: rel.targetEntity?.name,
          health: rel.overallHealth,
          confidence: rel.confidence,
          isConfirmed: rel.isConfirmed,
          evidence: rel.evidence?.length || 0,
        };
      });

      const dataStr = JSON.stringify(selectedData, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });

      const link = document.createElement('a');
      link.href = URL.createObjectURL(dataBlob);
      link.download = `relationships_export_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  if (selectedCount === 0) return null;

  return (
    <>
      <Paper
        elevation={8}
        sx={{
          position: 'fixed',
          bottom: 16,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1300,
          p: 2,
          minWidth: 400,
          maxWidth: 600,
        }}
      >
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SpeedIcon color="primary" />
            <Typography variant="subtitle1" fontWeight="bold">
              Batch Actions
            </Typography>
            <Chip
              label={`${selectedCount} selected`}
              size="small"
              color="primary"
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title="Select all filtered">
              <IconButton size="small" onClick={handleSelectAll}>
                <SelectAllIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Deselect all">
              <IconButton size="small" onClick={handleDeselectAll}>
                <DeselectIcon />
              </IconButton>
            </Tooltip>
            <IconButton
              size="small"
              onClick={(e) => setAnchorEl(e.currentTarget)}
            >
              <MoreIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Selection Stats */}
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          {selectionStats.confirmed > 0 && (
            <Chip label={`${selectionStats.confirmed} confirmed`} size="small" color="success" />
          )}
          {selectionStats.unconfirmed > 0 && (
            <Chip label={`${selectionStats.unconfirmed} unconfirmed`} size="small" color="warning" />
          )}
          {selectionStats.highConfidence > 0 && (
            <Chip label={`${selectionStats.highConfidence} high confidence`} size="small" color="info" />
          )}
          {selectionStats.criticalHealth > 0 && (
            <Chip label={`${selectionStats.criticalHealth} critical health`} size="small" color="error" />
          )}
        </Stack>

        {/* Primary Actions */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {selectionStats.unconfirmed > 0 && (
            <Button
              variant="contained"
              color="success"
              startIcon={<CheckIcon />}
              onClick={() => handleOperationClick(BULK_OPERATIONS[0])}
            >
              Confirm ({selectionStats.unconfirmed})
            </Button>
          )}

          <Button
            variant="outlined"
            color="error"
            startIcon={<CloseIcon />}
            onClick={() => handleOperationClick(BULK_OPERATIONS[1])}
          >
            Reject ({selectedCount})
          </Button>

          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => handleOperationClick(BULK_OPERATIONS[2])}
          >
            Update Confidence
          </Button>

          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExportSelected}
          >
            Export
          </Button>
        </Box>

        {/* Processing Indicator */}
        {isProcessing && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Processing {operationDialog?.label}...
            </Typography>
            <LinearProgress variant="determinate" value={processingProgress} />
          </Box>
        )}

        {/* Operation Result */}
        {operationResult && (
          <Alert
            severity={operationResult.failed === 0 ? 'success' : 'warning'}
            sx={{ mt: 2 }}
            onClose={() => setOperationResult(null)}
          >
            <Typography variant="body2">
              {operationResult.operation} completed: {operationResult.successful} successful, {operationResult.failed} failed
            </Typography>
            {operationResult.error && (
              <Typography variant="body2" color="error">
                Error: {operationResult.error}
              </Typography>
            )}
          </Alert>
        )}
      </Paper>

      {/* More Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem onClick={() => handleSelectFiltered('unconfirmed')}>
          <FilterIcon sx={{ mr: 1 }} />
          Select Unconfirmed Only
        </MenuItem>
        <MenuItem onClick={() => handleSelectFiltered('high_confidence')}>
          <FilterIcon sx={{ mr: 1 }} />
          Select High Confidence
        </MenuItem>
        <MenuItem onClick={() => handleSelectFiltered('critical')}>
          <FilterIcon sx={{ mr: 1 }} />
          Select Critical Health
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => handleOperationClick(BULK_OPERATIONS[3])}>
          <AssignmentIcon sx={{ mr: 1 }} />
          Assign Reviewer
        </MenuItem>
      </Menu>

      {/* Operation Dialog */}
      <Dialog
        open={Boolean(operationDialog)}
        onClose={() => setOperationDialog(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {operationDialog?.label}
        </DialogTitle>

        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {operationDialog?.description}
          </Typography>

          <Alert severity="info" sx={{ mb: 2 }}>
            This action will affect {selectedCount} selected relationships.
          </Alert>

          {/* Operation-specific fields */}
          {operationDialog?.key === 'edit_confidence' && (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>New Confidence Level</InputLabel>
              <Select
                value={newConfidence}
                onChange={(e) => setNewConfidence(e.target.value)}
                label="New Confidence Level"
              >
                {CONFIDENCE_LEVELS.map(level => (
                  <MenuItem key={level} value={level}>
                    {level}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {operationDialog?.key === 'assign_reviewer' && (
            <TextField
              fullWidth
              label="Reviewer Email"
              value={assigneeEmail}
              onChange={(e) => setAssigneeEmail(e.target.value)}
              sx={{ mb: 2 }}
              placeholder="reviewer@faanaworks.com"
            />
          )}

          {/* Optional note field */}
          <TextField
            fullWidth
            label="Note (Optional)"
            multiline
            rows={3}
            value={operationNote}
            onChange={(e) => setOperationNote(e.target.value)}
            placeholder="Add a note about this batch operation..."
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOperationDialog(null)}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmOperation}
            variant="contained"
            color={operationDialog?.color || 'primary'}
            disabled={
              isProcessing ||
              (operationDialog?.key === 'assign_reviewer' && !assigneeEmail)
            }
          >
            {isProcessing ? 'Processing...' : `Apply to ${selectedCount} relationships`}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default BatchActionsBar;